import { useState, useEffect, useMemo, useRef } from 'react';
import { musikkAPI } from '@/services/api';
import type { Musikk, MusikkTransponering } from '@/services/api';
import { tolkSang } from '@/lib/sang/tolkSang';
import { tolkGrunntone, transponerToneart, visToneart } from '@/lib/sang/toneart';
import type { VisValg } from '@/lib/sang/visSang';

interface UseTranspositionFormProps {
  musikk: Musikk | null;
  lagrede: MusikkTransponering[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onReload: () => void;
}

/**
 * Skjemaet på transponeringssiden. En lagret transponering av en tall-sang betyr bokstaver i den
 * tonearten, så både vellykket lagring og «Last inn» setter «Vis som» til Bokstaver for tall-sang.
 * Bokstav-sang får bare tonearten.
 */
export const useTranspositionForm = ({
  musikk,
  lagrede,
  onSuccess,
  onError,
  onReload,
}: UseTranspositionFormProps) => {
  const [transposeBy, setTransposeBy] = useState('0');
  const [visning, setVisning] = useState<VisValg['visning']>('original');
  const [saving, setSaving] = useState(false);

  const tekst = musikk?.tekst_og_akkorder || '';
  const erTallsang = useMemo(() => tolkSang(tekst).notasjon === 'tall', [tekst]);
  // Sangen skjemaet viser nå. Et lagresvar for en sang brukeren har gått fra, skal ikke endre «Vis som».
  const gjeldende = useRef(musikk);

  useEffect(() => {
    gjeldende.current = musikk;
    setTransposeBy('0');
    setVisning('original');
  }, [musikk]);

  /** Bokstaver krever toneart. Uten den står tallene, som i produksjonsplanen. */
  const visSomLagret = () => {
    if (erTallsang && tolkGrunntone(musikk?.grunntone, musikk?.toneart_type)) setVisning('bokstaver');
  };

  const lastInn = (semitoner: number) => {
    setTransposeBy(semitoner.toString());
    visSomLagret();
  };

  const handleSaveTransposition = async () => {
    const original = tolkGrunntone(musikk?.grunntone, musikk?.toneart_type);
    if (!musikk?.id || !original) return;

    const transpose = parseInt(transposeBy);
    if (lagrede.some((rad) => rad.semitoner === transpose)) {
      onError('Denne tonearten er allerede lagret');
      return;
    }

    try {
      setSaving(true);
      const ny = transponerToneart(original, transpose);

      const data = {
        navn: visToneart(ny),
        grunntone: ny.navn.replace(/m$/, ''),
        toneart_type: ny.slekt,
        semitoner: transpose,
      };

      await musikkAPI.createTransponering(musikk.id, data);
      if (gjeldende.current === musikk) visSomLagret();
      onSuccess('Transponering lagret');
      onReload();
    } catch (err: any) {
      console.error('Kunne ikke lagre transponering:', err);
      onError(err.response?.data?.error || 'Kunne ikke lagre transponering');
    } finally {
      setSaving(false);
    }
  };

  return {
    transposeBy,
    setTransposeBy,
    visning,
    setVisning,
    saving,
    erTallsang,
    lastInn,
    handleSaveTransposition,
  };
};
